import React from 'react'
import { PhotoIcon } from '@heroicons/react/24/outline'
import { adresZApiKonta } from '../../api/account'

/**
 * Karta wykonawcy jak na liście klienta w makiecie „VoltValue dla domu” (wersja 12, 2026-10-24): logo, nazwa
 * z niebieską rozetą certyfikatu, ocena i liczba opinii z Google, miejscowość, dwa zdjęcia realizacji z „+N zdjęć”
 * i kafelek „Specjalizuje się w”. Dane z `profil_wykonawcy` (backend `accounts/profil_wykonawcy.py`, kontrakt
 * w `zadania/2026-10_plan-rynek-dla-domu-produkcja.md` sekcja 4).
 *
 * Wspólna dla podglądu w ankiecie firmy (`WizytowkaWykonawcy`) i listy wykonawców przy zleceniu (pakiet P3): cenę,
 * termin, gwarancję i wybór dokłada rodzic przez `children` i `obokNazwy`. Atrybucję z logo Map Google pokazuje rodzic
 * raz pod listą (`ZrodloOcenGoogle`), jak w makiecie; karta sama tylko mówi „w Google”.
 *
 * Certyfikat to sam symbol (decyzja D21): opłacone Premium i zapisana ankieta, bez wpływu na kolejność.
 */

export const odmiana = (n, jeden, kilka, wiele) => (n === 1 ? jeden : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? kilka : wiele)
/** Dach skośny to podstawa montażu, nie specjalizacja (uwagi przeglądu D10): nie pokazujemy go w „Specjalizuje się w”. */
export const PODSTAWA_NIE_SPECJALIZACJA = /^dach(y)?\s+sko[śs]n(e|y)$/i
const ocenaTekst = (ocena) => ocena.toLocaleString('pl-PL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

const RZAD_GWIAZDEK = (kolor, rozmiar) => (
  <span className={`flex ${kolor}`}>
    {[0, 1, 2, 3, 4].map((i) => (
      <svg key={i} viewBox="0 0 24 24" className={`${rozmiar} flex-none`} aria-hidden="true"><path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z" fill="currentColor" /></svg>
    ))}
  </span>
)

/**
 * Pięć gwiazdek wypełnionych według oceny (3,1 na 5 to 62% szerokości). Rysunek jest nasz, a liczby z Google Maps, więc
 * obok zawsze stoi liczba; sam rysunek jest ozdobą dla czytnika ekranu.
 */
export const Gwiazdki = ({ ocena, rozmiar = 'h-3.5 w-3.5' }) => (
  <span aria-hidden="true" data-testid="gwiazdki" className="relative inline-flex flex-none align-[-2px]">
    {RZAD_GWIAZDEK('text-gray-300', rozmiar)}
    <span className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: `${Math.min(100, Math.max(0, (ocena / 5) * 100))}%` }}>
      {RZAD_GWIAZDEK('text-amber-500', rozmiar)}
    </span>
  </span>
)

export const Rozeta = ({ className = 'h-[18px] w-[18px]' }) => (
  <span role="img" aria-label="Certyfikat VoltValue" title="Certyfikat VoltValue. Nie wpływa na kolejność na liście."
        className="inline-flex flex-none cursor-help items-center">
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M12 1l2.4 2.1 3.1-.6 1 3 3 1-.6 3.1L23 12l-2.1 2.4.6 3.1-3 1-1 3-3.1-.6L12 23l-2.4-2.1-3.1.6-1-3-3-1 .6-3.1L1 12l2.1-2.4-.6-3.1 3-1 1-3 3.1.6z" fill="#2563EB" />
      <path d="M7.6 12.2l3 3 5.8-6" stroke="#FFFFFF" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
)

export const ZrodloOcenGoogle = ({ className = '' }) => (
  // Atrybucja wymagana przez Google przy ocenach (decyzja D22): oficjalne logo Map Google bez zmian, wysokość 16 px, wokół
  // niego pusty obszar z zasad Places API (10 px z boków i u góry, 5 px u dołu). Dwa pliki, bo w ciemnym motywie logo jest białe.
  <p className={`m-0 flex items-center gap-2.5 pb-[5px] pt-2.5 text-xs text-gray-600 ${className}`} style={{ fontFamily: 'Roboto, Arial, sans-serif' }}>
    Oceny z
    <span role="img" aria-label="Mapy Google" translate="no" className="inline-flex flex-none">
      <img src="/google/GoogleMaps_Logo_Gray.svg" alt="" className="h-4 w-auto dark:hidden" />
      <img src="/google/GoogleMaps_Logo_White.svg" alt="" className="hidden h-4 w-auto dark:block" />
    </span>
  </p>
)

const Logo = ({ nazwa, url }) => (
  // Białe tło także w ciemnym motywie (jak w makiecie): logo firm bywa ciemne albo z przezroczystym tłem.
  <span className="flex h-[38px] w-[68px] flex-none items-center justify-center overflow-hidden rounded-lg border border-gray-200 p-1"
        style={{ background: '#FFFFFF' }}>
    {url
      ? <img src={adresZApiKonta(url)} alt={`Logo ${nazwa}`} className="max-h-full max-w-full object-contain" />
      : <span aria-hidden="true" className="text-base font-bold text-blue-700">{(nazwa || '?').trim().charAt(0).toUpperCase()}</span>}
  </span>
)

const Zdjecie = ({ zdjecie, alt, wiecej }) => (
  <span className="relative block min-w-0 overflow-hidden rounded-[10px] bg-gray-100">
    <img src={zdjecie.url} alt={alt} loading="lazy" className="block h-full w-full object-cover" />
    {zdjecie.podpis && (
      <span className="absolute bottom-1.5 left-1.5 max-w-[calc(100%-12px)] truncate rounded-full bg-slate-900/70 px-2 py-0.5 text-[11px] font-semibold text-white">
        {zdjecie.podpis}
      </span>
    )}
    {wiecej > 0 && (
      <span className="absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-slate-900/70 px-2 py-0.5 text-[11px] font-bold text-white">
        <PhotoIcon className="h-3 w-3 flex-none" aria-hidden="true" />
        +{wiecej} {odmiana(wiecej, 'zdjęcie', 'zdjęcia', 'zdjęć')}
      </span>
    )}
  </span>
)

const KartaWykonawcy = ({ nazwa, miasto, odleglosc, profil, obokNazwy, children, className = '' }) => {
  const p = profil || {}
  const zdjecia = (p.zdjecia || []).slice(0, 2)
  const wiecej = Math.max(0, (p.liczba_zdjec || 0) - zdjecia.length)
  const google = p.google && p.google.ocena != null && p.google.liczba_opinii > 0 ? p.google : null
  const specjalizacje = (p.specjalizacje || []).filter((x) => !PODSTAWA_NIE_SPECJALIZACJA.test(String(x).trim())).slice(0, 4)
  const gdzie = [miasto, odleglosc].filter(Boolean).join(', ')
  return (
    <article className={`flex flex-col gap-2.5 rounded-2xl border-[1.5px] border-gray-200 bg-white p-3 ${className}`}>
      <div className="flex items-center gap-3">
        <Logo nazwa={nazwa} url={p.logo_url} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h3 className="m-0 flex min-w-0 items-center gap-1.5 text-[15px] font-bold text-gray-900">
            <span className="truncate">{nazwa}</span>
            {p.certyfikat && <Rozeta />}
          </h3>
          <p className="m-0 flex flex-col items-start text-[12.5px] leading-snug text-gray-600">
            {google && (
              <span>
                <a href={google.uri || undefined} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 font-bold text-gray-900 no-underline hover:underline">
                  <Gwiazdki ocena={google.ocena} />
                  {ocenaTekst(google.ocena)}<span className="sr-only"> na 5</span>
                </a>
                {' '}({google.liczba_opinii} {odmiana(google.liczba_opinii, 'opinia', 'opinie', 'opinii')} w Google)
              </span>
            )}
            {gdzie && <span>{gdzie}</span>}
          </p>
        </div>
        {obokNazwy}
      </div>
      {zdjecia.length > 0 && (
        <div role="group" aria-label={`Realizacje: ${nazwa}`}
             className={`grid h-[96px] gap-1.5 max-[640px]:h-[88px] ${zdjecia.length === 2 ? 'grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]' : 'grid-cols-1'}`}>
          {zdjecia.map((z, i) => (
            <Zdjecie key={z.id ?? z.url} zdjecie={z} wiecej={i === zdjecia.length - 1 ? wiecej : 0}
                     alt={z.podpis ? `${z.podpis}, realizacja: ${nazwa}` : `Realizacja: ${nazwa}`} />
          ))}
        </div>
      )}
      {specjalizacje.length > 0 && (
        <span className="self-start rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-semibold leading-5 text-blue-700">
          Specjalizuje się w: {specjalizacje.join(', ')}
        </span>
      )}
      {children}
    </article>
  )
}

export default KartaWykonawcy
